import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { Grid } from "@mantine/core";
import { IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { cloneDeep } from "lodash";
import { GenericTable, RowsConfig2 } from "./GenericTable";
import {
  CashflowItems,
  IncomesItem,
  OutgoingItem,
} from "../../../backend/types-v2";
import { hUserId, jUserId } from "../../../backend/data-v2";
import { saveUserFormData } from "../../../firebase";
import { AppContext } from "../../../Context";
import { baseFormDefaults } from "./baseFormDefaults";
import { FakeAccordion } from "./dataTables/FakeAccordion";
import { RecurrenceFields } from "./dataTables/RecurrenceFields";
import {
  CashflowAdapter,
  incomesFormAdapter,
  outgoingsFormAdapter,
} from "./dataTables/adapters";
import {
  amountRow,
  bonusesRow,
  descriptionRow,
  makeTaxProfileRow,
  parentalLeaveRow,
  redundancyRow,
  scheduleRow,
} from "./dataTables/rows";
import { byType } from "./dataTables/utils";

type TaxProfile = {
  userId: string;
  userName: string;
};

const taxProfileIds: TaxProfile[] = [
  {
    userId: jUserId,
    userName: "J-ID",
  },
  {
    userId: hUserId,
    userName: "H-ID",
  },
];

const createCashflowRowConfig = <
  TItem extends IncomesItem | OutgoingItem,
>(args: {
  allOriginalItems: CashflowItems;
  adapter: CashflowAdapter<TItem>;
  rows: RowsConfig2<TItem>["rows"];
  taxProfileIds: TaxProfile[];
  setUserData: Dispatch<SetStateAction<CashflowItems>>;
}): RowsConfig2<TItem> => {
  return {
    form: {
      getDefaultValue: {
        ...baseFormDefaults,
        isTaxable: args.adapter.capabilities.taxable,
        taxProfileId: null,
      },

      getInitialValue: args.adapter.toForm,

      onSubmitAddItem: async (form) => {
        const newItem = args.adapter.fromForm(form);

        const newItems = cloneDeep(args.allOriginalItems);
        newItems.push(newItem);

        await saveUserFormData(newItems);
        args.setUserData(newItems);
      },

      onSubmitEditItem: async (form, updatedId) => {
        console.log("🚀 ~ createCashflowRowConfig ~ form:", form, updatedId);
        const updatedItem = args.adapter.fromForm(form, updatedId);
        const newItems = args.allOriginalItems.map((x) =>
          x.id === updatedId ? updatedItem : x,
        );
        console.log("🚀 ~ createCashflowRowConfig ~ newItems:", newItems);

        await saveUserFormData(newItems);

        console.log("saved!");
        args.setUserData(newItems);
      },

      onSubmitDeleteItem: async ({ id: deletedId }) => {
        const updatedItems = args.allOriginalItems.filter(
          (item) => item.id !== deletedId,
        );

        await saveUserFormData(updatedItems);
        args.setUserData(updatedItems);
      },

      renderFields: (form) => (
        <RecurrenceFields
          form={form}
          taxProfileIds={args.taxProfileIds}
          capabilities={args.adapter.capabilities}
        />
      ),
    },

    rows: args.rows,
  };
};

export function DataTablesV2() {
  const ctx = useContext(AppContext);

  const [isOpenIncomes, setIsOpenIncomes] = useState(false);
  const [isOpenOutcomes, setIsOpenOutcomes] = useState(false);

  const incomes = ctx.userData.filter(byType("INCOME_ITEM"));
  const outgoings = ctx.userData.filter(byType("OUTGOINGS_ITEM"));

  const incomesRowConfig = createCashflowRowConfig({
    allOriginalItems: ctx.userData,
    adapter: incomesFormAdapter,
    rows: [
      descriptionRow,
      amountRow,
      makeTaxProfileRow(taxProfileIds),
      scheduleRow,
      parentalLeaveRow,
      redundancyRow,
      bonusesRow,
    ],
    taxProfileIds,
    setUserData: ctx.setUserData,
  });

  const outgoingsRowConfig = createCashflowRowConfig({
    allOriginalItems: ctx.userData,
    adapter: outgoingsFormAdapter,
    rows: [descriptionRow, amountRow, scheduleRow],
    taxProfileIds,
    setUserData: ctx.setUserData,
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <FakeAccordion
          title="Incomes"
          itemCount={incomes.length}
          colour="green"
          Icon={IconTrendingUp}
          isOpen={isOpenIncomes}
          setIsOpen={setIsOpenIncomes}
        >
          <GenericTable rowsConfig={incomesRowConfig} items2={incomes} />
        </FakeAccordion>
      </Grid.Col>
      <Grid.Col span={6}>
        <FakeAccordion
          title="Outgoings"
          itemCount={outgoings.length}
          colour="red"
          Icon={IconTrendingDown}
          isOpen={isOpenOutcomes}
          setIsOpen={setIsOpenOutcomes}
        >
          <GenericTable rowsConfig={outgoingsRowConfig} items2={outgoings} />
        </FakeAccordion>
      </Grid.Col>
    </Grid>
  );
}
